// slideshow-inline-blaze.js

const { Core, Utils, onBlazeLoad } = Global;
const { $hide, $replaceContent, $toggleDisplay } = Utils;

const ACTIVE_CLASSNAME = 'blaze-active';

function _activeToggle(state) {
  const action = state ? 'add' : 'remove';
  return (el) => {
    el.classList[action](ACTIVE_CLASSNAME);
  };
}

customElements.define('slideshow-inline-blaze', class extends Core {
  elements = {
    $: ['blaze', 'slides-wrapper', ['nav']]
  }

  propTypes = {
    gap: String,
    'slides-per-view': Number,
    'slides-per-view-mobile': Number,
    'slide-by-row': Boolean,
    'swipe-on-desktop': Boolean,
    'track-active': Boolean,
    'inert': Boolean
  }

  render() {
    // if slideshow is inert, it will activate only on reset. Handy for dynamically loaded content
    if (this.prop('inert')) {
      return;
    }

    onBlazeLoad(() => {
      this._initBlaze();

      // Listen for Product Quick View lifecycle to toggle draggable
      this._onQVOpen = () => this._setDragging(false); // force draggable: false
      this._onQVClose = () => this._setDragging(true);  // restore draggable: true (or original behavior)

      window.addEventListener('quick-view:open', this._onQVOpen);
      window.addEventListener('quick-view:close', this._onQVClose);
    });
  }

  destroy() {
    if (this.slider) {
      this.slider.destroy();
    }
    // Clean up listeners if they were attached
    if (this._onQVOpen) window.removeEventListener('quick-view:open', this._onQVOpen);
    if (this._onQVClose) window.removeEventListener('quick-view:close', this._onQVClose);
  }

  replaceSlides(slides) {
    this.$('slides-wrapper').replaceChildren(...slides);
    this.reset();
  }

  reset() {
    if (!this.slider) {
      this.removeAttribute('inert');
      this._initBlaze();
    }
    this.slider.totalSlides = this.slider.slides.length;
    this.slider.refresh();
    this._toggleNavigation();
  }

  _initBlaze() {
    if (!window.BlazeSlider) {
      // sanity check
      return;
    }

    this.slider = new window.BlazeSlider(this.$('blaze'), this._buildOpts());

    if (this.prop('track-active')) {
      this._handleActiveSlides();
    }
    this._toggleNavigation();
  }

  // -----------------------
  // DRAGGABLE CONTROL (NEW)
  // -----------------------
  /**
   * Builds Blaze options. If forceDraggable is provided (true/false) we use it
   * for both desktop and mobile; otherwise fall back to component props.
   */
  _buildOpts(forceDraggable) {
    const desktopDraggable = (forceDraggable !== undefined)
      ? !!forceDraggable
      : !!this.prop('swipe-on-desktop'); // original desktop behavior

    const mobileDraggable = (forceDraggable !== undefined)
      ? !!forceDraggable
      : true; // original mobile behavior = always draggable

    return {
      all: {
        slideGap: this.prop('gap'),
        loop: false,
        slidesToShow: this.prop('slides-per-view'),
        slidesToScroll: this.prop('slide-by-row') ? this.prop('slides-per-view') : 1,
        draggable: desktopDraggable, // <— desktop draggable
      },
      '(max-width: 992px)': {
        slidesToShow: this.prop('slides-per-view-mobile'),
        slidesToScroll: 1,
        draggable: mobileDraggable,  // <— mobile draggable
      }
    };
  }

  /**
   * Re-initializes Blaze with draggable explicitly set to state (true/false).
   * Preserves current index and active tracking state.
   */
  _setDragging(state) {
    if (!this.$('blaze')) return;

    const currentIndex = this.slider?.state?.index ?? 0;

    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }

    // Recreate slider with forced draggable option
    this.slider = new window.BlazeSlider(this.$('blaze'), this._buildOpts(state));

    // Restore position
    if (typeof this.slider.slideTo === 'function') {
      this.slider.slideTo(currentIndex);
    }

    // Restore active tracking if enabled
    if (this.prop('track-active')) {
      this._handleActiveSlides();
      // Ensure active classes reflect current page after re-init
      const [start, end] = this.slider.states[0].page;
      this._updateActiveSlides(null, start, end);
    }

    this._toggleNavigation();

    // Defensive: prevent OS-level touch gestures when disabled
    this.$('blaze').style.touchAction = state ? '' : 'none';
  }

  // -----------------------
  // ACTIVE SLIDES TRACKING
  // -----------------------
  _handleActiveSlides() {
    this.$slides = Array.from(this.slider.slides);
    this._setInitialActiveSlides();
    this.slider.onSlide(this._updateActiveSlides.bind(this));
  }

  _setInitialActiveSlides() {
    const [start, end] = this.slider.states[0].page;
    this.activeSlides = this._findSlidesInRange(start, end);
    this._toggleActiveSlides(true);
  }

  _toggleActiveSlides(state) {
    this.activeSlides?.map(_activeToggle(state));
  }

  _findSlidesInRange(start, end) {
    return this.$slides.filter((_, i) => start <= i && end >= i);
  }

  _updateActiveSlides(_, start, end) {
    this._toggleActiveSlides(false);
    this.activeSlides = this._findSlidesInRange(start, end);
    this._toggleActiveSlides(true);
  }

  // -----------------------
  // NAV VISIBILITY
  // -----------------------
  _toggleNavigation() {
    this.$('nav')?.map(item => $toggleDisplay(item, this.hasOffset));
  }

  get hasOffset() {
    return this.slider.totalSlides > this.prop('slides-per-view');
  }
});
